import { ElementType } from "react";
import EditNoteIcon from "@mui/icons-material/EditNote";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ApiIcon from "@mui/icons-material/Api";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";

type SelectionConfig = {
  title: string;
  description: string;
  icon: ElementType;
};

const iconColorMap: Record<string, string> = {
  'Shopify Connect': 'text-green-600',
  'File Upload': 'text-blue-600',
  'Manual Entry': 'text-purple-600',
  'API Connection': 'text-orange-600',
  'Other Stores': 'text-rose-600'
};

const existingProviderSelectionConfig: SelectionConfig[] = [
  {
    title: "Shopify Connect",
    description: "Connect your Shopify store directly",
    icon: StorefrontIcon,
  },
  {
    title: "File Upload",
    description: "Upload your products using CSV or Excel",
    icon: CloudUploadIcon,
  },
  {
    title: "Manual Entry",
    description: "Add products one by one",
    icon: EditNoteIcon,
  },
  {
    title: "API Connection",
    description: "Connect using our API",
    icon: ApiIcon,
  },
  {
    title: "Other Stores",
    description: "Connect Wayfair, WooCommerce, Temu and more",
    icon: IntegrationInstructionsIcon,
  }
];

type ExistingProviderSelectionViewProps = {
  selectedProviders: string[];
  setSelectedProviders: (selectedProviders: string[]) => void;
};

const ProviderCheckbox = ({
  title,
  description,
  Icon,
  selected,
  setSelected,
}: {
  title: string;
  description: string;
  Icon: ElementType;
  selected: boolean;
  setSelected: (selected: boolean) => void;
}) => {
  return (
    <button
      onClick={() => setSelected(!selected)}
      className={`w-full p-4 rounded-lg border-2 transition-all duration-200 flex items-center gap-4 hover:bg-blue-50 ${
        selected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
      }`}
    >
      <div className="flex-shrink-0">
        <Icon className={`w-8 h-8 ${selected ? 'text-blue-600' : iconColorMap[title] || 'text-gray-600'}`} />
      </div>
      <div className="text-left flex-grow min-w-0">
        <h3 className="font-semibold text-lg">{title}</h3>
        <p className="text-gray-600 text-sm">{description}</p>
      </div>
      <div className="flex-shrink-0 ml-4">
        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
          selected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
        }`}>
          {selected && (
            <div className="w-2 h-2 bg-white rounded-full" />
          )}
        </div>
      </div>
    </button>
  );
};

const ExistingProviderSelectionView = ({
  selectedProviders,
  setSelectedProviders,
}: ExistingProviderSelectionViewProps) => {
  return (
    <article className="w-full">
      <div className="grid grid-cols-1 gap-4">
        {existingProviderSelectionConfig.map((config) => (
          <ProviderCheckbox
            key={config.title}
            title={config.title}
            description={config.description}
            Icon={config.icon}
            selected={selectedProviders.includes(config.title)}
            setSelected={(selected) => {
              if (selected) {
                setSelectedProviders([...selectedProviders, config.title]);
              } else {
                setSelectedProviders(
                  selectedProviders.filter(
                    (provider) => provider !== config.title
                  )
                );
              }
            }}
          />
        ))}
      </div>
    </article>
  );
};

export default ExistingProviderSelectionView;